import { createContext, useContext, useState, useCallback } from 'react';
import {
  IMarketChartContextData,
} from '@/creditflowresearch/domain/vo/MarketChart/IMarketChartContextData';
import DealRepository from '@/creditflowresearch/infrastructure/repository/DealRepository';
import { getContainer } from '@framework/di/getContainer';
import { useRouter } from 'next/router';
import { DictionariesContext } from '@/creditflowresearch/application/Dictionaries/DictionariesContext';
import { useDictionariesContext } from '@/creditflowresearch/application/Dictionaries/v2/DictionariesContext';
import Segment from '@/creditflowresearch/domain/Segment';
import Market from '@/creditflowresearch/domain/Market';
import Product from '@/creditflowresearch/domain/Product';
import Region from '@/creditflowresearch/domain/Region';
import {
  initialMarketChartResponse,
} from '@/creditflowresearch/application/MarketChart/constants';
import { filterEmptyDeals } from '@/creditflowresearch/application/MarketChart/serverSide/helpers';
import * as FILTER_TYPES from '@/creditflowresearch/application/PageFiltersState/constants';
import useAutoFetch from '@/creditflowresearch/application/hooks/useAutoFetch';

const useCorporateMarketChart = (data) => {
  const [chartData, setChartData] = useState<IMarketChartContextData>(data);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const { query } = useRouter();
  const { products, regions, markets, segments } = query;
  const legacy = useContext(DictionariesContext);
  const { industries: v2Industries } = useDictionariesContext();
  const industries = legacy?.industries ?? v2Industries;

  const container = getContainer();
  const dealRepository = container.get(DealRepository);

  const preparePayload = useCallback(() => {
    const payload = {
      filters: {
        includedProductIds: Product.getIds(products),
        includedRegionIds: Region.getIds(regions),
        includedMarketIds: Market.getIds(markets),
        includedSegmentIds: Segment.getIds(segments),
      },
      groupBy: FILTER_TYPES.INDUSTRY,
    };

    return {
      ...data.preparedPayload,
      filters: {
        ...data.preparedPayload.filters,
        ...payload.filters,
      },
    };
  }, [products, regions, markets, segments, data.preparedPayload]);

  const getMarketChartData = useCallback(async (filters) => {
    setIsFetching(true);
    try {
      const { items, showVolume } = await dealRepository.getCorporateChartData(filters);
      const mappedItems = items
        // @ts-ignore TODO: incorrect type of payload with correct repository type
        .map((x: { industryId: string, data: { date: string, dealCount: number, size: number }[] }) => {
        const industry = industries.getById(x.industryId);
        return {
          dealChartData: x.data.map((item) => ({
            date: item.date,
            dealCount: item.dealCount,
            dealSize: item.size,
          })),
          name: industry?.name,
          color: industry?.color,
        };
      });
      setChartData({
        collection: filterEmptyDeals(mappedItems),
        showVolume,
      });
    } catch (e) {
      setChartData(initialMarketChartResponse);
    } finally {
      setIsFetching(false);
    }
  }, [data.preparedPayload]);

  useAutoFetch(() => {
    const payload = preparePayload();
    getMarketChartData(payload);
  }, [products, regions, markets, segments]);

  return {
    chartData: { ...chartData, isFetching },
    setChartData,
  };
};

type CorporateMarketChartContextType = ReturnType<typeof useCorporateMarketChart>;

interface IMarketChartContextProviderProps {
  children: React.ReactNode,
  chartData: IMarketChartContextData,
}

const CorporateMarketChartContext = createContext<CorporateMarketChartContextType>(null);

export const useCorporateMarketChartContext = () => {
  const context = useContext(CorporateMarketChartContext);
  if (!context) {
    throw new Error('useCorporateMarketChartContext must be used within the CorporateMarketChartContext');
  }
  return context;
};

const CorporateMarketChartContextProvider = ({ children, chartData }: IMarketChartContextProviderProps) => (
  <CorporateMarketChartContext.Provider value={ useCorporateMarketChart(chartData) }>
    { children }
  </CorporateMarketChartContext.Provider>
);

export default CorporateMarketChartContextProvider;
